import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { formatDate } from "../pages/appointment/helpers/formatDate";
import {
  getDefaultEndDateTime,
  getDefaultStartDateTime,
} from "../pages/appointment/helpers/constants";

export interface AppointmentFiltersState {
  practitioner: string[];
  status: string[];
  "appointment-type": string[];
  service: string[];
  start: string;
  end: string;
  view: "month" | "week" | "day" | "agenda";
  calendarDate: string;
}

const initialState: AppointmentFiltersState = {
  practitioner: [],
  status: [],
  "appointment-type": [],
  service: [],
  start: formatDate(getDefaultStartDateTime(new Date())) ?? "",
  end: formatDate(getDefaultEndDateTime(new Date())) ?? "",
  view:
    typeof window !== "undefined" && window.innerWidth < 700 ? "day" : "week",
  calendarDate: formatDate(getDefaultStartDateTime(new Date())) ?? "",
};

const slice = createSlice({
  name: "appointmentFilters",
  initialState,
  reducers: {
    setAppointmentFilters(
      state,
      action: PayloadAction<Partial<AppointmentFiltersState>>,
    ) {
      return { ...state, ...action.payload };
    },
    updateAppointmentFilter<K extends keyof AppointmentFiltersState>(
      state,
      action: PayloadAction<{ key: K; value: AppointmentFiltersState[K] }>,
    ) {
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };
    },
    resetAppointmentFilters() {
      return { ...initialState };
    },
    setAppointmentDateRange(
      state,
      action: PayloadAction<{ start: string | null; end: string | null }>,
    ) {
      return {
        ...state,
        ...(action.payload.start != null && { start: action.payload.start }),
        ...(action.payload.end != null && { end: action.payload.end }),
      };
    },
    setAppointmentView(
      state,
      action: PayloadAction<"month" | "week" | "day" | "agenda">,
    ) {
      return {
        ...state,
        view: action.payload,
      };
    },
    setAppointmentCalendarDate(state, action: PayloadAction<string | null>) {
      return {
        ...state,
        ...(action.payload != null && { calendarDate: action.payload }),
      };
    },
  },
});

export const {
  setAppointmentFilters,
  updateAppointmentFilter,
  resetAppointmentFilters,
  setAppointmentDateRange,
  setAppointmentView,
  setAppointmentCalendarDate,
} = slice.actions;

export const { reducer } = slice;

export default slice;
